/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ButtonBase } from '@coursera/cds-core';

import CoachIcon from 'bundles/ai-course-coach/assets/CoachIcon';
import { CoachBrandLabel } from 'bundles/ai-course-coach/assets/coachBranding';

import _t from 'i18n!nls/learning-assistant-chat-ai';

export type Props = {
  'aria-label'?: string;
  onClick: () => void;
} & React.ComponentPropsWithRef<'button'>;

const styles = css`
  --button-size: 36px;

  height: var(--button-size);
  width: 107px;
  position: fixed;
  bottom: 24px;
  right: 24px;
  border-radius: var(--cds-spacing-100) var(--cds-spacing-100) 2px var(--cds-spacing-100);
  background-color: #eaf3fe; /* todo: use new coach token */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0, 1, 0.9, 1);

  .coach-ondemand-button-container {
    overflow: hidden;
    position: relative;
    display: flex;
    gap: var(--cds-spacing-100);
    padding-left: var(--cds-spacing-100);
    justify-content: center;
    align-items: center;
    border-radius: inherit;
  }

  .coach-bubble {
    width: var(--button-size);
    height: var(--button-size);
    background-color: #3587fc1a; /* todo: use new coach token */
    border-radius: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .coach-icon,
  .coach-label {
    z-index: 1;
    transition: all 0.2s cubic-bezier(0, 1, 0.9, 1);
  }

  .hover-bubble {
    background-color: #1e72eb; /* todo: use new coach token */
    opacity: 0;
    height: 0;
    width: 0;
    transition: all 0.2s ease-out;
    border-radius: 50%;
    right: -20px;
    position: absolute;
    z-index: 0;
  }

  &:hover,
  &:focus-visible,
  &:active {
    .coach-label {
      path {
        fill: var(--cds-color-white-0);
      }
    }

    .coach-icon {
      circle {
        stroke: #3587fc; /* todo: use new coach token */
      }
    }

    .hover-bubble {
      width: 200px;
      height: 200px;
      opacity: 1;
    }
  }

  &.focusVisible {
    &::after {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border-radius: var(--cds-spacing-100) var(--cds-spacing-100) 2px var(--cds-spacing-100);
      box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
        0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  }

  &:active {
    transform: scale3d(0.98, 0.98, 0.98);
    border-radius: var(--cds-spacing-200);

    .coach-bubble,
    .hover-bubble {
      border-radius: var(--cds-spacing-200);
    }
  }
`;

/**
 * Trigger button for users to open up Coach chat on-demand.
 */
export const OnDemandButton = React.forwardRef((props: Props, ref: React.Ref<HTMLButtonElement>) => {
  const { onClick, 'aria-label': ariaLabel, ...rest } = props;

  return (
    <ButtonBase
      ref={ref}
      aria-label={ariaLabel || _t('Chat with Coursera Coach, your AI-powered learning assistant')}
      className="coach-ondemand-button"
      data-testid="coach-launcher-button"
      classes={{
        focusVisible: 'focusVisible',
      }}
      css={styles}
      onClick={onClick}
      {...rest}
    >
      <div className="coach-ondemand-button-container">
        <CoachBrandLabel className="coach-label" />
        <div className="coach-bubble">
          <CoachIcon />
          <div className="hover-bubble" />
        </div>
      </div>
    </ButtonBase>
  );
});

export default OnDemandButton;
